import { SheetManager } from "../taxform/SheetManager";
import { TaxFormBase } from "../taxform/TaxFormBase";
import Spec from "../specs/Spec";
import DECLARATIONS_BY_CONDITION from "./formList/Dec.json";
import moment from "moment";

// SPECIFIC
import CVAE_ACP from "./formList/Form_CVAE_ACP.json";
import CVAE_DEC from "./formList/Form_CVAE_DEC.json";
import DAS2_DEC from "./formList/Form_DAS2_DEC.json";
import IFU_DEC from "./formList/Form_IFU_DEC.json";
import IS_ACP from "./formList/Form_IS_ACP.json";
import IS_DEC from "./formList/Form_IS_DEC.json";
import IS_RBT from "./formList/Form_IS_RBT.json";
import LOY_DEC from "./formList/Form_LOY_DEC.json";
import RCM_DEC from "./formList/Form_RCM_DEC.json";
import TVA_BCA12_ACP from "./formList/Form_TVA_BCA12_ACP.json";
import TVA_BCA12_DEC from "./formList/Form_TVA_BCA12_DEC.json";
import TVA_BCA12_RBT from "./formList/Form_TVA_BCA12_RBT.json";
import TVA_CA3_DEC from "./formList/Form_TVA_CA3_DEC.json";
import TVA_CA3_RBT from "./formList/Form_TVA_CA3_RBT.json";
import TVA_CA12_ACP from "./formList/Form_TVA_CA12_ACP.json";
import TVA_CA12_DEC from "./formList/Form_TVA_CA12_DEC.json";
import TVA_CA12_RBT from "./formList/Form_TVA_CA12_RBT.json";
import { VatItemAssist, VatItemAuto } from "./declarationServices";
import { PreviousVatCredit } from "../types/taxformTypes";

export abstract class DeclarationBase extends TaxFormBase {
    declarationDB: {
        accountingPeriod: {
            id: string;
            start: Date;
            end: Date;
        };
        amountDeclared: number;
        mtaeStatus: string;
        mtaeId: string;
        mtaeData: any;
        company: {
            id: string;
        };
        data: any;
        date: Date;
        form: any;
        id: string;
        sheets: {
            id: string;
            data: any;
            form: any;
        }[];
        status: string;
        document: {
            id: string;
            status: string;
            date: Date;
        };
        startDate: Date;
        endDate: Date;
    };

    sheets: SheetManager[] = [];

    // SPECIFIC
    formsMapping = {
        CVAE_ACP,
        CVAE_DEC,
        DAS2_DEC,
        IFU_DEC,
        IS_ACP,
        IS_DEC,
        IS_RBT,
        LOY_DEC,
        RCM_DEC,
        TVA_BCA12_ACP,
        TVA_BCA12_DEC,
        TVA_BCA12_RBT,
        TVA_CA3_DEC,
        TVA_CA3_RBT,
        TVA_CA12_ACP,
        TVA_CA12_DEC,
        TVA_CA12_RBT,
    };

    amountToPay: number = 0;

    errorMessages: string[] = [];

    constructor(
        public companyId: string,
        public accountingPeriodId: string,
        public declarationId: string,
        public cerfaNumber: string,
        public year: string,
        public startDate: string,
        public endDate: string
    ) {
        super();
    }

    abstract getDeclarationQuery();

    abstract getDepositDetectionQuery();

    abstract getDeclarationFormQuery(year: string, cerfa: string);

    abstract saveOrDuplicateDeclarationQuery(
        decId: string,
        assistVatItems: VatItemAssist[],
        autoVatItems: VatItemAuto[],
        assistVatMode: boolean,
        autoVatMode: boolean,
        previousVatCredit: PreviousVatCredit,
        sheetsFromBundle: SheetManager[]
    );

    abstract deleteSheetQuery(sheetId: string);

    get is1330CVAEcerfaNumber(): boolean {
        return this.cerfaNumber === "1330CVAE";
    }

    get formsList(): any[] {
        let list: any = [];
        // SPECIFIC
        // 1330CVAE
        // sheets are automatically generated from bundle
        if (this.is1330CVAEcerfaNumber) {
            return list;
        }
        const declaration = DECLARATIONS_BY_CONDITION.flatMap(
            (conditionBlock) => conditionBlock.listDeclaration
        ).find((declarationList) => declarationList.note.includes(this.cerfaNumber));
        if (declaration) {
            const listForm: string = declaration.listForm;
            if (listForm && this.formsMapping[listForm]) {
                list.push(...this.formsMapping[listForm]);
            } else {
                console.error("Form list missing");
                debugger;
            }
        } else {
            console.error("Declaration not found");
            debugger;
        }
        return list;
    }

    get allForms(): any[] {
        const extractForms = (forms: any[]) => {
            return forms.flatMap((form) => {
                const current = form.cerfa ? [form] : [];
                const complements = form.complementForm
                    ? extractForms(form.complementForm)
                    : form.forms
                    ? extractForms(form.forms)
                    : [];
                return [...current, ...complements];
            });
        };
        return extractForms(this.formsList);
    }

    get mandatoryForms(): any[] {
        let forms: any = [];
        for (const form of this.formsList) {
            if (form.type === "main-form") {
                forms.push(form);
            } else if (
                form.type === "multiple-page" &&
                form.forms?.every((subForm) => subForm.type === "main-form")
            ) {
                form.forms.every((subForm) => forms.push(subForm));
            }
        }
        return forms;
    }

    get optionalForms(): any[] {
        return (
            this.formsList?.filter(
                (form: any) =>
                    form.type === "sub-form" ||
                    (form.type === "multiple-page" &&
                        form.forms?.every((subForm: any) => subForm.type === "sub-form"))
            ) || []
        );
    }

    get allOptionalForms(): any[] {
        const flattedOptionalForms: any[] = this.optionalForms.flatMap(
            (form) => form.forms || form
        );
        const superFlattedOptionalForms: any[] = [
            ...flattedOptionalForms,
            ...flattedOptionalForms.flatMap((form) => form.complementForm),
        ].filter((form) => form !== undefined);
        if (superFlattedOptionalForms?.length) {
            return superFlattedOptionalForms;
        }
        return [];
    }

    async afterInstanciate() {
        await this.getDeclaration();
        await this.getDepositDetection();
        await this.generateSheets();
        this.setFixedVariables();
        this.updateDependencies();
        this.update();
        return this;
    }

    async getDeclaration() {
        await this.getDeclarationQuery();
    }

    async getDepositDetection() {
        await this.getDepositDetectionQuery();
    }

    async getDeclarationForm(year: string, cerfa: string) {
        try {
            const form = await this.getDeclarationFormQuery(year, cerfa);
            return { ...form, year };
        } catch (error) {
            console.error("Error getting declaration form", error);
            return null;
        }
    }

    async generateSheets() {
        this.sheets = [];
        if (!this.declarationDB) {
            for (const form of this.mandatoryForms) {
                const cerfaNumber: string = form.cerfa;
                const declarationForm = await this.getDeclarationForm(this.year, cerfaNumber);
                if (!declarationForm) {
                    console.error({
                        type: "error",
                        message: `No declaration form found for ${cerfaNumber} in ${this.year}`,
                    });
                    debugger;
                    return;
                } else {
                    const newSheet = new SheetManager(
                        "",
                        declarationForm,
                        null,
                        null,
                        this,
                        false,
                        undefined,
                        undefined
                    );
                    this.sheets.push(newSheet);
                }
            }
        } else {
            if (!this.declarationDB?.sheets?.length) {
                return;
            }
            for (const dbSheet of this.declarationDB.sheets) {
                const allJson = {
                    ...dbSheet.form.form,
                    id: dbSheet.form.id,
                    image: dbSheet.form.image,
                    year: this.year,
                };
                // to avoid duplicates
                let newSheet: SheetManager = null;
                if (!this.sheets.find((sheet) => sheet.name === dbSheet.form.cerfaNumber)) {
                    newSheet = new SheetManager(
                        dbSheet.id,
                        allJson,
                        null,
                        null,
                        this,
                        false,
                        dbSheet.data,
                        undefined
                    );
                } else {
                    let additionalPageNumber = 2;
                    while (
                        !!this.sheets.find(
                            (sheet) =>
                                sheet.name === `${dbSheet.form.cerfaNumber}_${additionalPageNumber}`
                        )
                    ) {
                        additionalPageNumber++;
                    }
                    newSheet = new SheetManager(
                        dbSheet.id,
                        allJson,
                        null,
                        null,
                        this,
                        false,
                        dbSheet.data,
                        additionalPageNumber
                    );
                }
                //
                this.sheets.push(newSheet);
            }
            this.sortSheets();
        }
    }

    async saveOrDuplicateDeclaration(
        decId: string,
        assistVatItems: VatItemAssist[],
        autoVatItems: VatItemAuto[],
        assistVatMode: boolean,
        autoVatMode: boolean,
        previousVatCredit: PreviousVatCredit,
        sheetsFromBundle: SheetManager[]
    ) {
        const declaration = await this.saveOrDuplicateDeclarationQuery(
            decId,
            assistVatItems,
            autoVatItems,
            assistVatMode,
            autoVatMode,
            previousVatCredit,
            sheetsFromBundle
        );
        if (!this.declarationId && declaration?.id) {
            this.declarationId = declaration.id;
        }
        return this.declarationId;
    }

    async addSheet(year: string, cerfaNumber: string, update: boolean = true) {
        const declarationForm = await this.getDeclarationForm(year, cerfaNumber);
        if (!declarationForm) {
            debugger;
            console.error({
                type: "error",
                message: `No declaration form found for ${cerfaNumber} in ${year}`,
            });
            return;
        }
        // to avoid duplicates
        let newSheet: SheetManager = null;
        if (!this.sheets.find((sheet) => sheet.name === cerfaNumber)) {
            newSheet = new SheetManager(
                "",
                declarationForm,
                null,
                null,
                this,
                false,
                undefined,
                undefined
            );
        } else {
            let additionalPageNumber = 2;
            while (
                !!this.sheets.find(
                    (sheet) => sheet.name === `${cerfaNumber}_${additionalPageNumber}`
                )
            ) {
                additionalPageNumber++;
            }
            newSheet = new SheetManager(
                "",
                declarationForm,
                null,
                null,
                this,
                false,
                undefined,
                additionalPageNumber
            );
        }
        //
        this.sheets.push(newSheet);
        if (update) {
            this.sortSheets();
            this.updateDependencies();
            this.update();
        }
        return newSheet;
    }

    async createAdditionalPage(cerfaNumber: string) {
        await this.addSheet(this.year, cerfaNumber);
    }

    async deleteSheet({ name, index }: { name: string; index: number }) {
        // SPECIFIC
        // 2561 + 3310A + 3310TER + 3310TIC
        // these forms, except complementary pages, work in groups = delete one, delete the others

        // by beneficiary
        if (this.cerfaNumber === "2561") {
            const sheetMainId: string = this.sheets[index].id;
            if (sheetMainId) {
                await this.deleteSheetQuery(sheetMainId);
            }
            const sheetBisId: string = this.sheets[index + 1].id;
            if (sheetBisId) {
                await this.deleteSheetQuery(sheetBisId);
            }
            const sheetTerId: string = this.sheets[index + 2].id;
            if (sheetTerId) {
                await this.deleteSheetQuery(sheetTerId);
            }
            this.sheets.splice(index, 3);
            return;
        }

        const excludedPatterns: string[] = ["EX", "(", "_1X", "_2X", "_3X"];
        if (!excludedPatterns.some((pattern) => name.includes(pattern))) {
            // by form
            if (
                name.startsWith("3310A") ||
                name.startsWith("3310TER") ||
                name.startsWith("3310TIC")
            ) {
                // get sheets to delete
                const shortName: string = name.substring(0, 5);
                const sheetsToDelete: { id: string; name: string; index: number }[] = this.sheets
                    .map((sheet, i) => ({ id: sheet.id, name: sheet.cerfaNumber, index: i }))
                    .filter((sheet) => sheet.name.startsWith(shortName));
                // delete them from DB if id
                await Promise.all(
                    sheetsToDelete.map((sheet) => sheet.id && this.deleteSheetQuery(sheet.id))
                );
                // splice sheets, sort by descending index before splicing to avoid lag
                sheetsToDelete
                    ?.sort((a, b) => b.index - a.index)
                    .forEach((sheet) => {
                        this.sheets.splice(sheet.index, 1);
                    });
                return;
            }
        }

        // standard case
        const sheetId: string = this.sheets[index].id;
        if (sheetId) {
            await this.deleteSheetQuery(sheetId);
        }
        this.sheets.splice(index, 1);
    }

    updateDependencies() {
        for (const sheet of this.sheets) {
            sheet.updateDependencies(this.specs);
        }
    }

    update() {
        for (const sheet of this.sheets) {
            sheet.update();
        }
        // SPECIFIC
        // 2561
        // financial fields
        if (this.cerfaNumber === "2561") {
            this.copyAddressAndFinancialFields();
        }
        //
        this.amountToPay = this.calculateAmountToPay(); // simple getter is not reactive in this case
        this.errorMessages = this.generateErrorMessages();
    }

    sortSheets() {
        // SPECIFIC
        // 2561
        // forms are not sorted because work in groups
        if (this.cerfaNumber === "2561") {
            return;
        }
        //
        this.sheets.sort((a, b) => {
            if (a.cerfaNumber && b.cerfaNumber) {
                // optionals below
                if (!this.isOptionalForm(a.cerfaNumber) && this.isOptionalForm(b.cerfaNumber))
                    return -1;
                if (this.isOptionalForm(a.cerfaNumber) && !this.isOptionalForm(b.cerfaNumber))
                    return 1;
                // else by cerfaNumber
                if (a.cerfaNumber < b.cerfaNumber) return -1;
                if (a.cerfaNumber > b.cerfaNumber) return 1;
            }
            // else by creation date
            // @ts-ignore
            if (a.createdAt && b.createdAt) {
                // @ts-ignore
                if (a.createdAt < b.createdAt) return -1;
                // @ts-ignore
                if (a.createdAt > b.createdAt) return 1;
            }
            return 0;
        });
    }

    isOptionalForm(cerfaNumber: string): boolean {
        if (this.allOptionalForms?.find((form) => form.cerfa === cerfaNumber)) {
            return true;
        }
        return false;
    }

    copyAddressAndFinancialFields() {
        const sheetsLength: number = this.sheets?.length || 0;
        let sheet2561TERi: number = 2;
        while (sheet2561TERi + 1 <= sheetsLength) {
            const sheet2561TER: SheetManager = this.sheets[sheet2561TERi];
            if (sheet2561TER) {
                const specs: Spec[] = sheet2561TER.specs;
                if (specs) {
                    for (const spec of specs) {
                        const key: string = spec.key;
                        const keyTwoChar: string = key?.slice(0, 2);
                        if (["AA", "XA", "AE", "AG"].includes(keyTwoChar)) {
                            const sheet2561: SheetManager = this.sheets[sheet2561TERi - 2];
                            const sheet2561Specs: Spec[] = sheet2561?.specs;
                            const referenceField: Spec = sheet2561Specs?.find((s) => s.key === key);
                            if (referenceField) {
                                const val = Spec.getValue(referenceField);
                                if (val !== undefined) {
                                    spec.forceUserValue(val);
                                }
                            } else {
                                // field not found
                            }
                        }
                    }
                } else {
                    debugger;
                    console.error("specs not found");
                }
            } else {
                debugger;
                console.error("sheet not found");
            }
            sheet2561TERi += 3;
        }
    }

    // SPECIFIC
    // 2777 + 1329DEF
    // fixed variables
    setFixedVariables() {
        if (!["2777", "1329DEF"].includes(this.cerfaNumber)) {
            return;
        }

        const sheetsMapping: Record<string, string[]> = {
            "2777": ["2777_6", "2777_7"],
            "1329DEF": ["1329DEF_2"],
        };

        type FixedVariables = Record<string, number>;

        const fixedVariablesMapping: Record<string, (endDate: string) => FixedVariables> = {
            "2777": (endDate: string) => ({ C8: +endDate.slice(5, 7) + 1 }), // C8 = due month, not declaration month, then end date plus one
            "1329DEF": (endDate: string) => ({ C9: +endDate.slice(0, 4) }),
        };

        const endDate: string =
            this.endDate || moment(this.declarationDB?.endDate).format("YYYY-MM-DD");

        const sheetKeys: string[] = sheetsMapping[this.cerfaNumber] || [];

        const fixedVariables: FixedVariables = fixedVariablesMapping[this.cerfaNumber]?.(endDate);

        if (!fixedVariables) {
            debugger;
            console.error("setFixedVariables - fixed variables not created");
            return;
        }

        const sheets: SheetManager[] = sheetKeys
            .map((key) => this.sheets.find((sheet) => sheet.cerfaNumber === key))
            .filter((sheet) => sheet !== undefined);

        if (!sheets?.length) {
            debugger;
            console.error("setFixedVariables - sheets not found");
            return;
        }

        Object.entries(fixedVariables).forEach(([key, value]) => {
            sheets.forEach((sheet) => {
                const spec = sheet.specs?.find((spec) => spec.key === key);
                if (spec) {
                    spec.forceUserValue(value);
                } else {
                    debugger;
                    console.error(
                        `setFixedVariables - ${sheet.cerfaNumber} spec not found - key: ${key}`
                    );
                }
            });
        });
    }

    // SPECIFIC
    calculateAmountToPay(): number {
        if (this.sheets?.length) {
            switch (this.cerfaNumber) {
                case "3310CA3":
                    return (
                        Math.round(
                            Number(
                                this.sheets.find((s) => s.cerfaNumber === "3310CA3_2")?.fields["KE"]
                                    ?.value
                            )
                        ) || 0
                    );
                case "3514":
                    return (
                        Math.round(
                            Number(
                                this.sheets.find((s) => s.cerfaNumber === "3514")?.fields["HC"]
                                    ?.value
                            )
                        ) || 0
                    );
                case "3525BIS":
                    return (
                        Math.round(
                            Number(
                                this.sheets.find((s) => s.cerfaNumber === "3525BIS")?.fields["BD"]
                                    ?.value
                            )
                        ) || 0
                    );
                case "3517SCA12":
                    return (
                        Math.round(
                            Number(
                                this.sheets.find((s) => s.cerfaNumber === "3517SC_4")?.fields["SC"]
                                    ?.value
                            )
                        ) || 0
                    );
                case "3517BCA12":
                    return (
                        Math.round(
                            Number(
                                this.sheets.find((s) => s.cerfaNumber === "3517BCA_3")?.fields["NC"]
                                    ?.value
                            )
                        ) || 0
                    );
                case "2777":
                    return (
                        Math.round(
                            Number(
                                this.sheets.find((s) => s.cerfaNumber === "2777_7")?.fields["EA"]
                                    ?.value
                            )
                        ) || 0
                    );
                case "2571":
                    return (
                        Math.round(
                            Number(
                                this.sheets.find((s) => s.cerfaNumber === "2571")?.fields["FN"]
                                    ?.value
                            )
                        ) || 0
                    );
                case "2572":
                    return (
                        Math.round(
                            Number(
                                this.sheets.find((s) => s.cerfaNumber === "2572_3")?.fields["PL"]
                                    ?.value
                            )
                        ) || 0
                    );
                case "1329DEF":
                    return (
                        Math.round(
                            Number(
                                this.sheets.find((s) => s.cerfaNumber === "1329DEF_2")?.fields["JK"]
                                    ?.value
                            )
                        ) || 0
                    );
                case "1329AC":
                    return (
                        Math.round(
                            Number(
                                this.sheets.find((s) => s.cerfaNumber === "1329AC_2")?.fields["KA"]
                                    ?.value
                            )
                        ) || 0
                    );
                default:
                    return 0;
            }
        }
        return 0;
    }

    generateErrorMessages() {
        let messages: string[] = [];
        if (this.sheets.length) {
            // format check
            const checks: Checks = {
                // SPECIFIC
                // 3310TIC
                siretChecks: {
                    "3310TIC_1": { key: "AC_@L", cell: "C" },
                    "3310TIC_1EX1": { key: "AC_@L", cell: "C" },
                    "3310TIC_2": { key: "AV_@L", cell: "C" },
                    "3310TIC_2EX1": { key: "AV_@L", cell: "C" },
                    "3310TIC_3": { key: "BJ_@L", cell: "B" },
                    "3310TIC_3EX1": { key: "BJ_@L", cell: "B" },
                },
                apeChecks: {
                    "3310TIC_1": "AD",
                    "3310TIC_1EX1": "AD",
                    "3310TIC_2": "AW",
                    "3310TIC_2EX1": "AW",
                    "3310TIC_3": "BK",
                    "3310TIC_3EX1": "BK",
                },
                maxPercentageChecks: {
                    "3310TIC_1": "AP",
                    "3310TIC_1EX1": "AP",
                    "3310TIC_2": "BR",
                    "3310TIC_2EX1": "BR",
                    "3310TIC_3": "CH",
                    "3310TIC_3EX1": "CH",
                },
                keysToIgnore: {
                    "3310TIC_1": ["AS", "BT"],
                    "3310TIC_1EX1": ["AS", "BT"],
                    "3310TIC_2": ["BB", "BU"],
                    "3310TIC_2EX1": ["BB", "BU"],
                    "3310TIC_3": ["BQ", "BV"],
                    "3310TIC_3EX1": ["BQ", "BV"],
                },
            };

            for (const sheet of this.sheets) {
                const values: { [key: string]: { type: string; subtype: string } } = sheet.values;
                for (const [key, value] of Object.entries(sheet.fields)) {
                    const displayValue = value.displayValue;
                    if (!displayValue) {
                        // no check on empty cells
                        continue;
                    }
                    // SIRET check
                    const checkConfigSiret = checks.siretChecks[sheet.name];
                    if (checkConfigSiret && key === checkConfigSiret.key) {
                        this.checkSiret(
                            messages,
                            sheet.name,
                            checkConfigSiret.cell,
                            displayValue as string
                        );
                        continue;
                    }
                    // APE code check
                    const apeKey = checks.apeChecks[sheet.name];
                    if (apeKey && key === apeKey) {
                        this.checkAPEcode(messages, sheet.name, key, displayValue as string);
                        continue;
                    }
                    // max percentage check
                    const maxPercentageKey = checks.maxPercentageChecks[sheet.name];
                    if (maxPercentageKey && key === maxPercentageKey) {
                        this.checkMaxPercentage(messages, sheet.name, key, displayValue as string);
                        continue;
                    }
                    //
                    const fieldDetails: { type: string; subtype: string } = values[key];
                    const type: string = fieldDetails?.type;
                    if (!type) {
                        // no check on empty types
                        continue;
                    }
                    if (type === "date") {
                        const subtype = fieldDetails.subtype;
                        if (!subtype) {
                            // no check on empty subtypes
                            continue;
                        }
                        const message = this.checkDate(
                            sheet.name,
                            key,
                            subtype,
                            displayValue as string
                        );
                        if (message !== "ok") {
                            messages.push(message);
                            continue;
                        }
                    }
                    if (type !== "number") {
                        // no check on non number cells
                        continue;
                    }
                    if (String(displayValue).includes(",") || String(displayValue).includes(".")) {
                        messages.push(`${sheet.name}:${key} : nombres à virgule non autorisés`);
                        continue;
                    }
                    const numberDisplayValue: number = Number(displayValue);
                    if (numberDisplayValue === 0) {
                        // no check on zero values
                        continue;
                    }
                    if (!numberDisplayValue) {
                        messages.push(`${sheet.name}:${key} : lettres non autorisées`);
                        continue;
                    }
                    if (numberDisplayValue < 0) {
                        // negative values allowed
                        if (
                            checks.keysToIgnore[sheet.name] &&
                            checks.keysToIgnore[sheet.name].includes(key)
                        ) {
                            continue;
                        }
                        messages.push(`${sheet.name}:${key} : nombres négatifs non autorisés`);
                    }
                }
            }

            // SPECIFIC
            // 2572
            if (this.cerfaNumber === "2572") {
                this.check2572Cerfa(messages);
                return messages;
            }

            // SPECIFIC
            // 3310CA3
            if (this.cerfaNumber === "3310CA3") {
                this.check3310CA3Cerfa(messages);
                return messages;
            }

            // SPECIFIC
            // DAS2
            if (this.cerfaNumber === "DAS2") {
                this.checkDAS2Cerfa(messages);
                return messages;
            }
        }
        return messages;
    }

    checkDate(sheetName: string, key: string, subtype: string, value: string) {
        switch (subtype) {
            case "102":
                if (!/^(0[1-9]|[12][0-9]|3[01])(0[1-9]|1[0-2])\d{4}$/.test(value)) {
                    return `${sheetName}:${key} : date au format JJMMAAAA attendue`;
                }
                break;
            case "106":
                if (!/^(0[1-9]|[12][0-9]|3[01])(0[1-9]|1[0-2])$/.test(value)) {
                    return `${sheetName}:${key} : date au format JJMM attendue`;
                }
                break;
            case "602":
                if (!/^\d{4}$/.test(value)) {
                    return `${sheetName}:${key} : année au format AAAA attendue`;
                }
                break;
            case "718":
                if (
                    !/^(0[1-9]|[12][0-9]|3[01])(0[1-9]|1[0-2])\d{4}(0[1-9]|[12][0-9]|3[01])(0[1-9]|1[0-2])\d{4}$/.test(
                        value
                    )
                ) {
                    return `${sheetName}:${key} : période au format JJMMAAAAJJMMAAAA attendue`;
                }
                break;
            case "801":
                if (!/^\d+$/.test(value)) {
                    return `${sheetName}:${key} : durée en années attendue`;
                }
                break;
            case "802":
                if (!/^\d+$/.test(value)) {
                    return `${sheetName}:${key} : durée en mois attendue`;
                }
                break;
            default:
                return "ok";
        }
        return "ok";
    }

    checkSiret(messages: string[], sheetName: string, cell: string, value: string) {
        if (!/^\d{14}$/.test(value)) {
            messages.push(
                `${sheetName}:cellule ${cell} : un SIRET doit comporter 14 caractères numériques`
            );
        }
    }

    checkAPEcode(messages: string[], sheetName: string, key: string, value: string) {
        if (!/^\d{4}[A-Z]$/.test(value)) {
            messages.push(
                `${sheetName}:${key} : un code APE est composé de 4 chiffres puis 1 lettre majuscule`
            );
        }
    }

    checkMaxPercentage(messages: string[], sheetName: string, key: string, value: string) {
        if (+value < 0 || +value > 100) {
            messages.push(`${sheetName}:${key} : le pourcentage doit être compris entre 0 et 100`);
        }
    }

    check2572Cerfa(messages: string[]) {
        const sheet: SheetManager = this.sheets.find((sheet) => sheet.cerfaNumber === "2572_3");
        if (!sheet) {
            return;
        }
        const fields = sheet.fields;
        if (!fields) {
            return;
        }
        const valueEA: Spec = Spec.getValue(fields["EA"]);
        if (!valueEA) {
            return;
        }
        const valueDA: Spec = Spec.getValue(fields["DA"]);
        if (!valueDA || valueDA < valueEA) {
            messages.push("2572_3:EA : montant supérieur à l'excédent disponible");
        }
    }

    check3310CA3Cerfa(messages: string[]) {
        // sum check
        const fields = this.sheets[0].fields;
        const taxedOperationsFields = ["CA", "CB", "KH", "DK", "KV", "CH", "CC", "CF", "CG", "CE"];
        const taxedOperationsFieldsTotal = taxedOperationsFields.reduce((total, key) => {
            const field = fields[key];
            const value = Number(field?.value) || 0;
            return total + value;
        }, 0);
        const grossVatFields = [
            "FP",
            "FB",
            "FR",
            "FG",
            "FM",
            "FN",
            "FJ",
            "BQ",
            "BP",
            "NN",
            "BS",
            "BF",
            "BE",
            "MF",
            "MG",
            "FC",
            "GS",
            "GU",
            "LB",
            "LD",
            "LF",
            "LH",
            "LK",
            "LM",
        ];
        const grossVatFieldsTotal = grossVatFields.reduce((total, key) => {
            const field = fields[key];
            const value = Number(field?.value) || 0;
            return total + value;
        }, 0);
        if (taxedOperationsFieldsTotal - grossVatFieldsTotal !== 0) {
            messages.push(
                "Le total des opérations taxées ne correspond pas au total des bases hors taxes."
            );
        }
    }

    checkDAS2Cerfa(messages: string[]) {
        for (const sheet of this.sheets) {
            if (sheet.name.startsWith("DAS2TV")) {
                for (const spec of sheet.specs) {
                    if (spec.key.startsWith("BA")) {
                        const valueBA = Spec.getValue(spec);
                        if (valueBA) {
                            const specCA = sheet.specs.find(
                                (sp) => sp.key === spec.key.replace("BA", "CA")
                            );
                            const valueCA = Spec.getValue(specCA);
                            if (!valueCA) {
                                messages.push(
                                    `${sheet.name.replace(
                                        /_(\d+)$/,
                                        " ($1)"
                                    )} - Nature manquante pour le montant en cellule ${spec.key}`
                                );
                            }
                        }
                    }
                    if (spec.key === "AE_@I") {
                        const valueFirstAndLastName = Spec.getValue(spec);
                        if (valueFirstAndLastName) {
                            const specCompanyName = sheet.specs.find((sp) => sp.key === "AF_@I");
                            const valueCompanyName = Spec.getValue(specCompanyName);
                            if (valueCompanyName) {
                                messages.push(
                                    `${sheet.name.replace(
                                        /_(\d+)$/,
                                        " ($1)"
                                    )} - Un bénéficiaire est identifié soit par un nom et un prénom, soit par un SIRET et une raison sociale.`
                                );
                            }
                        }
                    }
                }
            } else if (sheet.name === "DAS2T") {
                for (const spec of sheet.specs) {
                    if (spec.key.startsWith("TA")) {
                        const valueTA = Spec.getValue(spec);
                        if (valueTA) {
                            const specUA = sheet.specs.find(
                                (sp) => sp.key === spec.key.replace("TA", "UA")
                            );
                            const valueUA = Spec.getValue(specUA);
                            if (!valueUA) {
                                messages.push(
                                    `DAS2T - Nature manquante pour le montant en cellule ${spec.key}`
                                );
                            }
                        }
                    }
                }
            }
        }
    }
}

type Checks = {
    siretChecks: Record<string, { key: string; cell: string }>;
    apeChecks: Record<string, string>;
    maxPercentageChecks: Record<string, string>;
    keysToIgnore: Record<string, string[]>;
};
